import { Card } from '@/components/ui/card';
import { FaFootballBall, FaMusic, FaTheaterMasks, FaCamera, FaBookReader, FaHandsHelping } from 'react-icons/fa';

const activities = [
  {
    title: "Sports et Compétitions",
    icon: <FaFootballBall className="text-5xl text-brand-primary" />,
    description: "Participez à nos équipes sportives et représentez CEFAT-BS dans les compétitions inter-universitaires. Football, basketball, volleyball et plus encore.",
    image: "https://images.unsplash.com/photo-1461896836934-ffe607ba8211?w=800"
  },
  {
    title: "Clubs Culturels",
    icon: <FaMusic className="text-5xl text-brand-primary" />,
    description: "Rejoignez nos clubs de musique, danse et théâtre. Exprimez votre créativité et participez à nos événements culturels annuels.",
    image: "https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=800"
  },
  {
    title: "Événements et Galas",
    icon: <FaTheaterMasks className="text-5xl text-brand-primary" />,
    description: "Des soirées gala, des journées portes ouvertes et des événements thématiques tout au long de l'année pour créer des souvenirs inoubliables.",
    image: "https://images.unsplash.com/photo-1511795409834-ef04bbd61622?w=800"
  },
  {
    title: "Associations Étudiantes",
    icon: <FaHandsHelping className="text-5xl text-brand-primary" />,
    description: "Engagez-vous dans des associations étudiantes et participez à des projets sociaux, environnementaux et communautaires.",
    image: "https://images.unsplash.com/photo-1559027615-cd4628902d4a?w=800"
  },
  {
    title: "Bibliothèque et Ressources",
    icon: <FaBookReader className="text-5xl text-brand-primary" />,
    description: "Accédez à notre bibliothèque moderne avec des milliers d'ouvrages, des espaces d'étude et des ressources numériques.",
    image: "https://images.unsplash.com/photo-1481627834876-b7833e8f5570?w=800"
  },
  {
    title: "Photographie et Média",
    icon: <FaCamera className="text-5xl text-brand-primary" />,
    description: "Rejoignez notre club média et capturez les moments forts de la vie universitaire. Développez vos compétences en photographie et vidéographie.",
    image: "https://images.unsplash.com/photo-1542038784456-1ea8e935640e?w=800"
  }
];

export default function VieEtudiantines() {
  return (
    <div className="min-h-screen bg-white py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 text-brand-secondary">Vie Estudiantines</h1>
          <p className="text-base sm:text-lg text-gray-600 max-w-3xl mx-auto">
            Une expérience universitaire complète au-delà des salles de classe
          </p>
        </div>

        {/* Hero Image */}
        <div className="mb-16 rounded-lg overflow-hidden shadow-2xl">
          <img 
            src="https://images.unsplash.com/photo-1523050854058-8df90110c9f1?w=1200"
            alt="Vie étudiante"
            className="w-full h-96 object-cover"
          />
        </div>

        {/* Introduction */}
        <div className="mb-16 max-w-4xl mx-auto text-center">
          <p className="text-base sm:text-lg text-gray-700 leading-relaxed mb-6">
            À l'Université CEFAT International, nous croyons que l'éducation va au-delà des cours académiques. La vie estudiantine offre une multitude d'opportunités pour développer vos compétences, créer des liens et vous épanouir personnellement.
          </p>
          <p className="text-base sm:text-lg text-gray-700 leading-relaxed">
            Que vous soyez passionné de sport, de culture, de technologie ou d'engagement social, vous trouverez votre place dans notre communauté dynamique et inclusive.
          </p>
        </div>

        {/* Activities Grid */}
        <div className="grid md:grid-cols-2 gap-8 mb-16">
          {activities.map((activity, index) => (
            <Card 
              key={index} 
              className="overflow-hidden card-hover bg-white border-gray-200 shadow-md"
              data-testid={`activity-card-${index}`}
            >
              <div className="h-48 overflow-hidden">
                <img 
                  src={activity.image} 
                  alt={activity.title}
                  className="w-full h-full object-cover transition-transform duration-300 hover:scale-110"
                />
              </div>
              <div className="p-6">
                <div className="flex items-center gap-4 mb-4">
                  {activity.icon}
                  <h3 className="text-2xl font-bold text-brand-secondary">{activity.title}</h3>
                </div>
                <p className="text-gray-700 leading-relaxed">
                  {activity.description}
                </p>
              </div>
            </Card>
          ))}
        </div>

        {/* Testimonial */}
        <div className="bg-gradient-to-r from-brand-primary/10 to-brand-secondary/10 p-12 rounded-lg mb-16">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-lg sm:text-xl text-gray-800 italic mb-6 leading-relaxed">
              "Mon expérience à l'Université CEFAT International a été transformatrice. Au-delà des cours, j'ai pu m'impliquer dans plusieurs clubs et associations qui m'ont permis de développer mes compétences en leadership et de créer des amitiés durables."
            </p>
            <div>
              <p className="font-bold text-brand-primary text-lg">Aminata Kouassi</p>
              <p className="text-gray-600">Diplômée 2023 - Marketing et Management</p>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center bg-brand-secondary text-white p-12 rounded-lg shadow-xl">
          <h2 className="text-2xl sm:text-3xl font-bold mb-4">Rejoignez notre communauté !</h2>
          <p className="text-base sm:text-lg mb-6">Découvrez tout ce que l'Université CEFAT International a à offrir</p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a href="https://scolarite-cefat.net/preinscription">
              <button 
                data-testid="vie-cta-preinscription"
                className="w-full sm:w-auto bg-white text-brand-secondary hover:bg-brand-accent font-bold px-6 sm:px-8 py-3 sm:py-4 rounded-md shadow-lg transition-all hover:shadow-xl hover:-translate-y-1"
              >
                Préinscription
              </button>
            </a>
            <a href="/contact">
              <button 
                data-testid="vie-cta-contact"
                className="w-full sm:w-auto bg-brand-primary hover:bg-brand-primary-dark text-white font-bold px-6 sm:px-8 py-3 sm:py-4 rounded-md shadow-lg transition-all hover:shadow-xl hover:-translate-y-1"
              >
                Contactez-nous
              </button>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}